/**
 * DEVLOGS-TEMP — level numbering and namespace filtering.
 *
 * Levels are pino's exact numbers so that a single threshold reads both the
 * api-server's pino lines and every client record (`ERROR_LEVEL = 50` in
 * artifacts/api-server/src/lib/logger.ts).
 */

import type { Level } from "./types.ts";

export const LEVELS = {
  trace: 10,
  debug: 20,
  info: 30,
  warn: 40,
  error: 50,
  fatal: 60,
} as const;

export type LevelName = keyof typeof LEVELS;

const NAME_BY_LEVEL: Record<number, LevelName> = {
  10: "trace",
  20: "debug",
  30: "info",
  40: "warn",
  50: "error",
  60: "fatal",
};

export function levelName(level: Level): LevelName {
  return NAME_BY_LEVEL[level] ?? "info";
}

/** Short uppercase tag used by the compact text renderer. */
export function levelTag(level: Level): string {
  switch (level) {
    case 10:
      return "TRC";
    case 20:
      return "DBG";
    case 30:
      return "INF";
    case 40:
      return "WRN";
    case 50:
      return "ERR";
    case 60:
      return "FTL";
    default:
      return "INF";
  }
}

/**
 * Accepts a name ("warn"), a number (40) or a numeric string ("40").
 * Anything unrecognised yields the fallback — never a throw, never NaN.
 */
export function parseLevel(
  raw: string | number | null | undefined,
  fallback: Level,
): Level {
  if (typeof raw === "number") return coerceLevel(raw, fallback);
  if (typeof raw !== "string") return fallback;
  const trimmed = raw.trim().toLowerCase();
  if (trimmed.length === 0) return fallback;
  if (trimmed in LEVELS) return LEVELS[trimmed as LevelName];
  const numeric = Number(trimmed);
  if (Number.isFinite(numeric)) return coerceLevel(numeric, fallback);
  return fallback;
}

function coerceLevel(value: number, fallback: Level): Level {
  const rounded = Math.round(value);
  if (rounded in NAME_BY_LEVEL) return rounded as Level;
  return fallback;
}

// --- namespace filter -------------------------------------------------------

export interface NamespaceFilter {
  readonly source: string;
  test(ns: string): boolean;
}

/**
 * The `debug`-package idiom, because it is the one everybody already knows:
 * comma-separated, `*` matches any suffix, a leading `-` excludes.
 *
 *   "*"                      everything (the dev default)
 *   "media.*,net.fetch"      only those
 *   "*,-media.timeupdate"    everything except one firehose
 *
 * Later rules win over earlier ones, so the exclusion idiom above works.
 * Verdicts are memoised into a map that CLEARS past a cap — an unbounded map
 * keyed by caller-supplied strings is how a logger becomes the leak.
 */
const MEMO_CAP = 256;

export function parseNamespaceFilter(
  spec: string | null | undefined,
): NamespaceFilter {
  const source = typeof spec === "string" ? spec.trim() : "";
  const rules = source
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .map((part) => {
      const negate = part.startsWith("-");
      const body = negate ? part.slice(1) : part;
      const prefix = body.endsWith("*") ? body.slice(0, -1) : null;
      return { negate, body, prefix };
    });

  const memo = new Map<string, boolean>();

  function evaluate(ns: string): boolean {
    let verdict = false;
    for (const rule of rules) {
      const hit =
        rule.prefix !== null ? ns.startsWith(rule.prefix) : ns === rule.body;
      if (hit) verdict = !rule.negate;
    }
    return verdict;
  }

  return {
    source,
    test(ns: string): boolean {
      if (rules.length === 0) return false;
      const cached = memo.get(ns);
      if (cached !== undefined) return cached;
      const verdict = evaluate(ns);
      if (memo.size >= MEMO_CAP) memo.clear();
      memo.set(ns, verdict);
      return verdict;
    },
  };
}
